import queryString from 'query-string';
import { path } from 'ramda';
import { takeLatest, put, select, call } from 'redux-saga/effects';

import { theme, createStandaloneToast } from '@frontend/components';

import quoteService from 'services/quoteService';

import history from 'utils/browserHistory';
import { getDigitalQuoteUrl } from 'utils/quoteUtils';

import * as actions from './actions';
import * as selectors from './reducer';

const { toast } = createStandaloneToast({ theme });

const types = actions.types;

export default function* root() {
  yield takeLatest(
    types.CONFIRM_QUOTE_ITEM_CONFIGURATION,
    watchQuoteItemConfigurationConfirmed,
  );
  yield takeLatest(types.REQUEST_A_REQUOTE, watchRequestARequote);
  yield takeLatest(types.SHARE_QUOTE, watchShareQuote);
  yield takeLatest(types.COPY_QUOTE_LINK_REQUEST, watchCopyQuoteLink);
  yield takeLatest(
    types.UPDATE_QUOTE_ITEM_CONFIGRATION,
    watchUpdateQuoteItemConfiguration,
  );
}

// the previously selected options can be gone at this point.
// we should exclude such options from selectedOptions
const ensureSelectedOptions = (selectedOptions, quoteItems) => {
  return Object.keys(selectedOptions).reduce((acc, quoteItemId) => {
    const option = selectedOptions[quoteItemId];
    const quoteItem = quoteItems.find((q) => q.id == quoteItemId);
    if (!quoteItem) return acc;
    const quantity = quoteItem.rootComponent.quantities.find(
      (q) => q.id == option.quantityId,
    );
    if (!quantity) return acc;
    if (option.expediteId) {
      const expedite = quantity.expedites.find(
        (e) => e.id == option.expediteId,
      );
      if (!expedite) return acc;
    }
    (!option.addOnCellIds || option.addOnCellIds.length == 0) &&
      (option.addOnCellIds = quoteItem.rootComponent.addOnCells
        .filter(
          (cell) =>
            cell.quantityId == quantity.id &&
            quoteItem.rootComponent.addOns.some(
              (addOn) => addOn.id == cell.addOnId && addOn.isRequired,
            ),
        )
        .map((cell) => cell.id));
    acc[quoteItemId] = option;
    return acc;
  }, {});
};

export function* rehydrateSelectedOptionsFromUrlParams(quoteItems, params) {
  const selectedOptions = {};
  for (const param in params) {
    const value = params[param];
    if (param.startsWith('add_on_cell_ids')) {
      const quoteItemId = param.split('add_on_cell_ids_')[1];
      !selectedOptions[quoteItemId] && (selectedOptions[quoteItemId] = {});
      const addOnCellIds = value.split('_').map((i) => parseInt(i));
      selectedOptions[quoteItemId].addOnCellIds = [...addOnCellIds];
    } else {
      const quoteItemId = param.split('quote_item_')[1];
      if (quoteItemId) {
        const [rawQuantityId, rawExpediteId] = value.split('_');
        !selectedOptions[quoteItemId] && (selectedOptions[quoteItemId] = {});
        selectedOptions[quoteItemId].quantityId = parseInt(rawQuantityId);
        selectedOptions[quoteItemId].expediteId = rawExpediteId
          ? parseInt(rawExpediteId)
          : null;
      }
    }
  }
  const ensured = ensureSelectedOptions({ ...selectedOptions }, quoteItems);
  yield put(actions.initSelectedOptions({ selectedOptions: ensured }));
  return ensured;
}

export function* initDigiQuotePage({ match, location }) {
  try {
    const token = match.params.token;
    const urlParams = queryString.parse(location.search);
    const isPreview = !!urlParams.preview || !!urlParams.facilitate;
    const isFacilitating = !!urlParams.facilitate;

    const sendConfirmationEmail = !!urlParams.with_confirmation;
    const quote = yield call(quoteService.getByToken, token, isPreview);
    yield call(
      rehydrateSelectedOptionsFromUrlParams,
      quote.quoteItems,
      urlParams,
    );

    yield put(
      actions.initDigiQuote({
        ...quote,
        isFacilitating,
        isPreview,
        sendConfirmationEmail,
        token,
      }),
    );
    const quoteId = yield select(selectors.getQuoteId);
    yield put(
      actions.receiveQuoteItems(
        quote.quoteItems.map((qi) => ({ ...qi, quoteId })),
      ),
    );
  } catch (error) {
    return { error };
  }
}

function* watchQuoteItemConfigurationConfirmed(action) {
  const { token, params } = action.payload;
  const isContactSelected = !!(yield select(selectors.getContact));
  if (isContactSelected) {
    yield put(actions.setHasSubmitted());
    const paramsStr = queryString.stringify(params);
    const url =
      `/checkout/?token=${token}` + (paramsStr ? '&' + paramsStr : '');
    history.push(url);
  } else {
    yield call(toast, {
      status: 'warning',
      title: 'Warning',
      description:
        "You can't proceed to checkout without " +
        'a contact specified for this quote.',
    });
  }
}

function* watchRequestARequote() {
  try {
    const token = yield select(selectors.getToken);
    const response = yield call(quoteService.requestARequote, token);
    yield put(actions.requestARequote.success(response));
  } catch (error) {
    yield put(actions.requestARequote.error({ error }));
  }
}

function getParams() {
  const location = window.location;
  const urlParams = queryString.parse(location.search);
  const facilitate = urlParams.facilitate === 'true';
  const preview = urlParams.preview === 'true';
  // Disable because this is intended for URL query parameters
  // eslint-disable-next-line camelcase
  const with_confirmation = urlParams.with_confirmation === 'true';
  if (facilitate) {
    return {
      facilitate,
      // eslint-disable-next-line camelcase
      with_confirmation,
    };
  } else {
    return { preview };
  }
}

function* generateDigitalQuoteLink({ isFull, isExpired }) {
  const token = yield select(selectors.getToken);
  if (!token) return;
  const quote = yield select(selectors.getQuote);
  const groupId = path(['locator'], quote);
  const selectedOptionByQuoteItemId = yield select(
    selectors.getQuoteItemConfiguration,
  );
  const params = getParams();
  if (!isExpired) {
    Object.keys(selectedOptionByQuoteItemId).forEach((quoteItemId) => {
      const option = selectedOptionByQuoteItemId[quoteItemId];
      if (option.quantityId) {
        params[`quote_item_${quoteItemId}`] = `${option.quantityId}${
          option.expediteId ? `_${option.expediteId}` : ''
        }`;
        option.addOnCellIds &&
          option.addOnCellIds.length > 0 &&
          (params[`add_on_cell_ids_${quoteItemId}`] =
            option.addOnCellIds.join('_'));
      }
    });
  }
  return getDigitalQuoteUrl(token, groupId, { params, full: isFull });
}

export function* watchShareQuote() {
  const quote = yield select(selectors.getQuote);
  const digitalQuoteUrl = yield call(generateDigitalQuoteLink, {
    isFull: true,
    isExpired: quote.expired && !quote.isFacilitating,
    isPreview: false,
  });
  yield put(actions.shareQuoteModal.open({ digitalQuoteUrl }));
}

function* watchCopyQuoteLink(action) {
  try {
    const { linkInput } = action.payload;
    linkInput.select();
    document.execCommand('copy');
    yield put(actions.copyQuoteLink.success());
  } catch (e) {
    yield put(actions.copyQuoteLink.error());
  }
}

export function* watchUpdateQuoteItemConfiguration() {
  const quote = yield select(selectors.getQuote);
  const params = getParams();
  const digitalQuoteUrl = yield call(generateDigitalQuoteLink, {
    isFull: false,
    isExpired: quote.expired && !quote.isFacilitating,
    ...params,
  });
  if (digitalQuoteUrl && window.history.replaceState) {
    window.history.replaceState({}, null, digitalQuoteUrl);
  }
}
